import apiClient from './apiClient';

export interface Message {
  id: number;
  title: string;
  content: string;
  messageType: 'global' | 'session_specific';
  targetSessionId?: number;
  isActive: boolean;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  createdByName?: string;
  createdByEmail?: string;
  sessionTitle?: string;
}

export interface MessageWithReadStatus extends Message {
  isRead: boolean;
  readAt?: string;
}

export interface CreateMessageData {
  title: string;
  content: string;
  messageType: 'global' | 'session_specific';
  targetSessionId?: number;
  isActive?: boolean;
}

export interface UpdateMessageData {
  title?: string;
  content?: string;
  messageType?: 'global' | 'session_specific';
  targetSessionId?: number;
  isActive?: boolean;
}

export interface MessageFilters {
  messageType?: 'global' | 'session_specific';
  targetSessionId?: number;
  isActive?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// Admin endpoints
export const createMessage = async (data: CreateMessageData): Promise<ApiResponse<Message>> => {
  const response = await apiClient.post('/api/messages', data);
  return response.data;
};

export const updateMessage = async (id: number, data: UpdateMessageData): Promise<ApiResponse<Message>> => {
  const response = await apiClient.put(`/api/messages/${id}`, data);
  return response.data;
};

export const deleteMessage = async (id: number): Promise<ApiResponse<void>> => {
  const response = await apiClient.delete(`/api/messages/${id}`);
  return response.data;
};

export const getAllMessages = async (filters?: MessageFilters): Promise<ApiResponse<Message[]>> => {
  const params = new URLSearchParams();
  if (filters?.messageType) params.append('messageType', filters.messageType);
  if (filters?.targetSessionId) params.append('targetSessionId', filters.targetSessionId.toString());
  if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());

  const response = await apiClient.get(`/api/messages/admin/all?${params.toString()}`);
  return response.data;
};

export const getMessage = async (id: number): Promise<ApiResponse<Message>> => {
  const response = await apiClient.get(`/api/messages/admin/${id}`);
  return response.data;
};

export const toggleMessageStatus = async (id: number): Promise<ApiResponse<Message>> => {
  const response = await apiClient.patch(`/api/messages/${id}/toggle-status`);
  return response.data;
};

// Student/User endpoints
export const getUserMessages = async (): Promise<ApiResponse<MessageWithReadStatus[]>> => {
  const response = await apiClient.get('/api/messages/user/messages');
  return response.data;
};

export const markMessageAsRead = async (id: number): Promise<ApiResponse<void>> => {
  const response = await apiClient.patch(`/api/messages/${id}/read`);
  return response.data;
};

export const markMessageAsUnread = async (id: number): Promise<ApiResponse<void>> => {
  const response = await apiClient.patch(`/api/messages/${id}/unread`);
  return response.data;
};

export const getUnreadMessageCount = async (): Promise<ApiResponse<{ unreadCount: number }>> => {
  const response = await apiClient.get('/api/messages/user/unread-count');
  return response.data;
};